import { useEffect, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import SpinnerOverlay from '../SpinnerOverlay';
import { getStartOfSchoolYear } from './reportUtilities';
import { getConcerningEmotionsReport } from '../BackendInterface';
import { buildReportRows } from '../ConcerningEmotionsReportRows';
import { buildSectionSummary } from '../ConcerningEmotionsReportSummary';
import { getEndOfDay } from '../ConcerningEmotionsReportUtilities';
import ConcerningEmotionsReportTable from '../ConcerningEmotionsReportTable';
import ConcerningEmotionsReportFilters, { ALL } from '../ConcerningEmotionsReportFilters';
import ConcerningEmotionsReportHeader from '../ConcerningEmotionsReportHeader';
import ConcerningEmotionsReportSummaryWidget from '../ConcerningEmotionsReportSummaryWidget';
import { gradeToGradeBand, GradeBand } from '@peekapak/types/EmotionsClassification';
import { EmotionsReportClassroom } from '@peekapak/types/EmotionsReportRow';
import styles from '../ConcerningEmotionsReport.module.scss';

const ConcerningEmotionsReportAdmin = (): JSX.Element => {
  const [classrooms, setClassrooms] = useState<EmotionsReportClassroom[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedClassroomId, setSelectedClassroomId] = useState(ALL);
  const [selectedGrade, setSelectedGrade] = useState(ALL);
  const [isDisplayDayPicker, setIsDisplayDayPicker] = useState(false);

  const today = useMemo(() => new Date(), []);
  const startOfSchoolYear = useMemo(() => getStartOfSchoolYear(), []);
  const [dateRange, setDateRange] = useState<DateRange>({ from: startOfSchoolYear, to: today });

  useEffect(() => {
    let mounted = true;

    getConcerningEmotionsReport()
      .then((data) => {
        if (mounted) {
          setClassrooms(data);
        }
      })
      .catch(() => {
        if (mounted) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (mounted) {
          setIsLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const classOptions = useMemo(
    () => [{ id: ALL, name: 'By class' }, ...classrooms.map((c) => ({ id: c.classroomId, name: c.classroomName }))],
    [classrooms],
  );

  const gradeOptions = useMemo(() => {
    const uniqueGrades = [...new Set(classrooms.flatMap((c) => c.students.map((s) => s.grade)))].sort((a, b) => a - b);
    return [
      { id: ALL, name: 'By grade' },
      ...uniqueGrades.map((grade) => ({ id: String(grade), name: String(grade) })),
    ];
  }, [classrooms]);

  // Grade filtering is applied per-student (not per-classroom) so a mixed-grade
  // classroom doesn't get excluded/included as a whole for a single-grade selection.
  const filteredClassrooms = useMemo(() => {
    return classrooms
      .filter((c) => selectedClassroomId === ALL || c.classroomId === selectedClassroomId)
      .map((c) => ({
        ...c,
        students: selectedGrade === ALL ? c.students : c.students.filter((s) => String(s.grade) === selectedGrade),
      }));
  }, [classrooms, selectedClassroomId, selectedGrade]);

  const selectedRange = useMemo(
    () => ({
      start: (dateRange.from ?? startOfSchoolYear).getTime(),
      end: getEndOfDay(dateRange.to ?? today),
    }),
    [dateRange, startOfSchoolYear, today],
  );

  const sections: { title: string; gradeBand: GradeBand }[] = [
    { title: 'PK-5 check-ins', gradeBand: 'PK-5' },
    { title: 'Grade 6-12 check-ins', gradeBand: '6-12' },
  ];

  return (
    <div className={styles.container}>
      <ConcerningEmotionsReportHeader />
      <ConcerningEmotionsReportFilters
        classOptions={classOptions}
        gradeOptions={gradeOptions}
        selectedClassroomId={selectedClassroomId}
        setSelectedClassroomId={setSelectedClassroomId}
        selectedGrade={selectedGrade}
        setSelectedGrade={setSelectedGrade}
        dateRange={dateRange}
        setDateRange={setDateRange}
        startOfSchoolYear={startOfSchoolYear}
        today={today}
        isDisplayDayPicker={isDisplayDayPicker}
        setIsDisplayDayPicker={setIsDisplayDayPicker}
        isLoading={isLoading}
      />
      {isLoading ? (
        <SpinnerOverlay isShow />
      ) : loadError ? (
        <p role='alert'>Unable to load the concerning emotions report. Please try again.</p>
      ) : (
        sections.map(({ title, gradeBand }) => {
          const classroomsInBand = filteredClassrooms.filter((c) => gradeToGradeBand(c.grade) === gradeBand);
          const rows = buildReportRows(classroomsInBand, selectedRange);
          const summary = buildSectionSummary(classroomsInBand, gradeBand, selectedRange);

          return (
            <div key={gradeBand} className={styles.section}>
              <h2 className={styles.sectionTitle}>{title}</h2>
              <ConcerningEmotionsReportSummaryWidget summary={summary} gradeBand={gradeBand} />
              <ConcerningEmotionsReportTable rows={rows} showTeacherColumn />
            </div>
          );
        })
      )}
    </div>
  );
};

export default ConcerningEmotionsReportAdmin;
